import React, {useState} from 'react';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Toolbar,
  Typography
} from '@mui/material';
import {lighten} from '@mui/material/styles'
import {withStyles} from '@mui/styles'
import clsx from 'clsx';
import {DeleteIcon, MoveIcon, TagIcon} from '../../../../../helpers/Icons'
import FolderSelector from '../../folders/FolderSelector';
import DataPaginationSmall from "../../../../util/data-table/DataPaginationSmall";
import CloseIcon from "@mui/icons-material/Close";

const styles = (theme) => ({
  root: {
    padding: '0.25em 0.5em',
    gap: '0.5em',
    minHeight: 'auto',
    border: '1px solid #ddd',
    borderBottom: 'none',
    position:'sticky',
    left: 0,
    display:'flex',
    flexWrap:'wrap',
    backgroundColor: '#fcfcfc',
  },
  highlight: {
    color: theme.palette.secondary.main,
    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
  },
  title: {
    marginRight: '0.75em',
    fontSize: '0.7225em',
    fontWeight: 500,
    whiteSpace:'nowrap',
    textTransform: 'uppercase',
  },
  toolbar: {
    flexWrap:'wrap',
    margin: '1px 0 0 1px',
    '& > button':{
      margin: '-1px 0 0 -1px',
      borderColor: '#c4c4c4 !important',
      width:'5rem',
      boxSizing: 'border-box',
    }
  },
  toolbarButton: {
    padding: '5.1px 10.2px',
    fontSize: '0.7225em',
    backgroundColor: '#ffffffdd',
    height: '34px',
    color: 'rgba(0, 0, 0, 0.87)',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    '& span:first-child':{
      marginLeft: '-3.4px',
      marginRight: '6.8px'
    },
    '& svg':{
      width: '1em',
      height: '1em',
    }
  },
});

function orderFolders(folderList, selectedItems){
  if(!selectedItems || selectedItems.length === 0){
    return folderList;
  }
  const selectedFolderIds = new Set(Object.values(selectedItems).map(item => item.folder.id));
  return [...folderList.filter(f => !selectedFolderIds.has(f.id)), ...folderList.filter(f => selectedFolderIds.has(f.id))]
}

function SelectedItemsToolbar(props) {
  const {pageItemCount, selectedItems, folderList, addFolder, handleMoveSelected, handleDeleteSelected, handleTagSelected,
      controller, itemCount, handlePageChange, handleChangeRowsPerPage, mindfileUserLevel, classes } = props;

  const [selectedFolder, setSelectedFolder] = useState(folderList[0]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const selectedCount = Object.keys(selectedItems).length;
  return (
    <>
      <Toolbar id="selectedToolbar" className={clsx(classes.root, {[classes.highlight]: selectedCount > 0,})} >
        {mindfileUserLevel <= 2 ?
          <>
            <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
              <span id="selectedCount">{selectedCount}</span> selected
            </Typography>
            <ButtonGroup className={classes.toolbar}>
              <Button
                id="tagSelected"
                startIcon={<TagIcon />}
                className={classes.toolbarButton}
                onClick={handleTagSelected}
                disabled={selectedCount === 0}
                variant="outlined"
              >
                Tag
              </Button>
              <Button
                id="moveSelected"
                startIcon={<MoveIcon />}
                className={classes.toolbarButton}
                onClick={() => {
                  setSelectedFolder(orderFolders(folderList, selectedItems)[0]);
                  setMoveOpen(true)}
                }
                disabled={selectedCount === 0}
                variant="outlined"
              >
                Move
              </Button>
              {mindfileUserLevel <= 1 ?
                <Button
                  id="deleteSelected"
                  startIcon={<DeleteIcon />}
                  className={classes.toolbarButton}
                  onClick={() => {setDeleteOpen(true)}}
                  disabled={selectedCount === 0}
                  variant="outlined"
                >
                  Delete
                </Button>
                : null
              }
            </ButtonGroup>
          </>
          : null
        }
        <DataPaginationSmall
          controller={controller}
          itemsTotal={itemCount}
          pageItemCount={pageItemCount}
          handlePageChange={handlePageChange}
          handleChangeRowsPerPage={handleChangeRowsPerPage} />
      </Toolbar>
      {mindfileUserLevel <= 2 ?
        <Dialog
          PaperProps={{id:"moveSelectedDialog"}}
          onClose={() => {setMoveOpen(false)}}
          open={moveOpen}
        >
          <DialogTitle>
            <div style={{display:'flex', flexWrap:'wrap-reverse', justifyContent:'space-between'}}>
              Move {selectedCount} {selectedCount > 1 ? 'Items' : 'Item'}
              <IconButton
                onClick={() => {
                  setMoveOpen(false);
                  setSelectedFolder(null);
                }}
                style={{width:'2rem', height:'2rem', margin:'-0.5rem 0 0 auto'}}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Move {selectedCount} {selectedCount > 1 ? 'items' : 'item'} to another folder.
            </DialogContentText>
            <div style={{width: '25em'}}>
              <FolderSelector
                label="Folder"
                selectedFolder={selectedFolder}
                folderList={folderList}
                addFolder={addFolder}
                handleFolderSelect={(newVal) => setSelectedFolder(newVal)}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleMoveSelected(selectedFolder.id);
                setMoveOpen(false);
                setSelectedFolder(null);
              }}
            >
              Move
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setMoveOpen(false);
                setSelectedFolder(null);
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
        : null
      }
      {mindfileUserLevel <= 1 ?
        <Dialog
          PaperProps={{id:"deleteSelectedDialog"}}
          onClose={() => {setDeleteOpen(false)}}
          open={deleteOpen}
        >
          <DialogTitle>
            <div style={{display:'flex', flexWrap:'wrap-reverse', justifyContent:'space-between'}}>
              Delete {selectedCount} {selectedCount > 1 ? 'Items' : 'Item'}
              <IconButton onClick={() => {setDeleteOpen(false)}} style={{width:'2rem', height:'2rem', margin:'-0.5rem 0 0 auto'}}>
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {selectedCount > 1 ?
                "Are you sure you want to permanently delete " + selectedCount + " items? This cannot be undone."
                : 'Are you sure you want to permanently delete this item? This cannot be undone.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleDeleteSelected();
                setDeleteOpen(false);
              }}
            >
              Delete
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {setDeleteOpen(false)}}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
        : null
      }
    </>
  );
}

export default withStyles(styles)(SelectedItemsToolbar);
